import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Badge } from '../../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import PageHeader from '../../../components/layout/PageHeader';
import {
  ArrowLeft, AlertTriangle, CheckCircle2, Download, Loader2, Pencil,
  RefreshCw, RotateCcw, Shield, TrendingDown, TrendingUp, XCircle,
} from 'lucide-react';
import { EditHouseViewDialog } from '../../../components/committee/HouseViewBuilder';
import {
  classForVerdict, formatPaise, groupArtifactsByPhase,
} from '../../../lib/committeeHelpers';
import { foldJsonInMarkdown, promoteBoldLinesToHeadings } from '../../../lib/jsonFold';
import AgentGraph from '../../../components/workspace/AgentGraph';

// Strip a ```json … ``` fenced block from raw LLM text. Used in places
// where we rely on the structured_json payload and only want the prose
// preamble (no JSON whatsoever).
function stripJsonFence(text) {
  if (!text || typeof text !== 'string') return '';
  return text.replace(/```(?:json)?\s*[\s\S]*?```/g, '').trim();
}

function MD({ children, foldJson = true }) {
  if (!children) return null;
  let content = String(children);
  if (foldJson) content = foldJsonInMarkdown(content);
  content = promoteBoldLinesToHeadings(content);
  return (
    <div className="prose prose-sm dark:prose-invert max-w-none break-words leading-relaxed
                    prose-headings:font-semibold prose-headings:text-foreground
                    prose-h1:text-lg prose-h1:mt-6 prose-h1:mb-3 prose-h1:pb-2 prose-h1:border-b prose-h1:border-border
                    prose-h2:text-base prose-h2:mt-6 prose-h2:mb-2 prose-h2:text-primary
                    prose-h3:text-sm prose-h3:mt-5 prose-h3:mb-2 prose-h3:uppercase prose-h3:tracking-wide prose-h3:text-muted-foreground
                    prose-p:my-3 prose-p:leading-relaxed
                    prose-ul:my-3 prose-ul:pl-5 prose-ol:my-3 prose-ol:pl-5
                    prose-li:my-1.5
                    prose-strong:text-foreground
                    prose-pre:max-w-full prose-pre:overflow-x-auto prose-pre:whitespace-pre-wrap
                    prose-pre:break-words prose-pre:text-xs prose-pre:bg-muted/50
                    prose-code:before:hidden prose-code:after:hidden
                    first:prose-headings:mt-0">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Always wrap inside a constrained, scrollable container even
          // for very wide JSON blobs so they don't push the card wider.
          pre: ({ node, ...props }) => (
            <pre
              {...props}
              className="overflow-x-auto whitespace-pre-wrap break-words text-xs"
            />
          ),
        }}
      >{content}</ReactMarkdown>
    </div>
  );
}

// A fact is "numeric" if it carries a numeric_value or unit — render those
// as compact stat chips up top for quick scanning.
function isNumericFact(f) {
  if (!f || typeof f !== 'object') return false;
  if (typeof f.numeric_value === 'number' && Number.isFinite(f.numeric_value)) return true;
  return false;
}

function fmtFactValue(f) {
  const v = f.numeric_value;
  if (typeof v !== 'number') return '';
  const unit = (f.unit || '').toUpperCase();
  if (unit === 'PCT' || unit === '%') return `${v.toFixed(Math.abs(v) < 10 ? 2 : 1)}%`;
  if (unit === 'INR_CR') return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })} cr`;
  if (unit === 'INR') return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  if (unit === 'RATIO') return `${v.toFixed(2)}x`;
  return Math.abs(v) >= 1000
    ? v.toLocaleString('en-IN', { maximumFractionDigits: 0 })
    : v.toFixed(2);
}

// Backtest-derived "track record" pill rendered next to the live verdict
// so the user sees how this committee's historical calls on this exact
// stock + verdict performed against the index. Hidden when no scored
// cells exist yet for the stock.
function TrackRecordBadge({ record, verdict }) {
  if (!record || !record.available || !record.n) return null;
  const v = (verdict || '').toUpperCase();
  const same = (record.by_verdict || []).find(
    (r) => (r.verdict || '').toUpperCase() === v,
  );
  // Prefer the same-verdict cohort when it has at least 3 datapoints; else
  // fall back to the overall stock-level summary so the badge always shows.
  const useSame = same && same.n >= 3;
  const n = useSame ? same.n : record.n;
  const hit = useSame ? same.hit_rate_pct : record.hit_rate_pct;
  const excess = useSame ? same.avg_excess_pct : record.avg_excess_pct;
  const ir = useSame ? same.info_ratio : record.info_ratio;
  const label = useSame ? `${v} (${n})` : `Overall (${n})`;
  const tone =
    excess > 1
      ? 'border-emerald-500/40 text-emerald-600 bg-emerald-500/5'
      : excess < -1
        ? 'border-rose-500/40 text-rose-600 bg-rose-500/5'
        : 'border-muted-foreground/30 text-muted-foreground bg-muted/30';
  const sign = excess >= 0 ? '+' : '';
  const tooltip =
    `Backtest track record on this stock @ 90d horizon\n` +
    `${useSame ? `Cohort: past ${v} verdicts (n=${n})` : `Cohort: all past verdicts (n=${n})`}\n` +
    `Hit rate: ${hit.toFixed(1)}%  •  Avg excess vs Nifty: ${sign}${excess.toFixed(2)}%  •  IR: ${ir.toFixed(2)}\n` +
    `Window: ${record.first_date || '?'} → ${record.last_date || '?'}`;
  return (
    <span
      title={tooltip}
      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-medium tabular-nums ${tone}`}
    >
      <span className="opacity-80">📊</span>
      <span>{label}</span>
      <span>·</span>
      <span>hit {hit.toFixed(0)}%</span>
      <span>·</span>
      <span>{sign}{excess.toFixed(1)}% vs Nifty</span>
    </span>
  );
}

function shortLabel(text, max = 36) {
  if (!text) return '';
  const trimmed = String(text).split(/[.,;:]/)[0].trim();
  return trimmed.length > max ? trimmed.slice(0, max - 1) + '…' : trimmed;
}

function NumericFactChips({ facts, max = 6 }) {
  const numeric = (facts || []).filter(isNumericFact).slice(0, max);
  if (numeric.length === 0) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 mb-3">
      {numeric.map((f, i) => (
        <div key={f.claim_id || i} className="rounded border border-border/60 p-2 bg-muted/20">
          <div className="text-[10px] text-muted-foreground uppercase tracking-wide truncate" title={f.text}>
            {shortLabel(f.text)}
          </div>
          <div className="text-sm font-semibold tabular-nums">{fmtFactValue(f)}</div>
          {f.period && <div className="text-[10px] text-muted-foreground">{f.period}</div>}
        </div>
      ))}
    </div>
  );
}

function FactsList({ facts, defaultOpen = false }) {
  if (!facts || facts.length === 0) return null;
  return (
    <details className="mt-3 text-sm" open={defaultOpen}>
      <summary className="cursor-pointer text-muted-foreground text-xs">
        All cited facts ({facts.length})
      </summary>
      <ul className="mt-2 space-y-1 text-xs">
        {facts.map((f, i) => (
          <li key={f.claim_id || i} className="flex gap-2 items-start">
            <span className="font-mono text-muted-foreground shrink-0">
              [{f.claim_id || `k${i + 1}`}]
            </span>
            <span className="flex-1">
              {f.text}
              {f.tool_name && (
                <span className="ml-2 text-blue-500">via {f.tool_name}</span>
              )}
              {f.period && (
                <span className="ml-2 text-muted-foreground">({f.period})</span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

// Pull bullet lines out of a markdown body so we can show a compact bullet list
// without forcing the user to read the full prose.
function extractBullets(md, max = 5) {
  if (!md) return [];
  const lines = String(md).split(/\r?\n/);
  const bullets = [];
  for (const ln of lines) {
    const m = ln.match(/^\s*(?:[-*•]|\d+\.)\s+(.+?)\s*$/);
    if (m && m[1].length > 4) bullets.push(m[1].replace(/\*\*/g, ''));
    if (bullets.length >= max) break;
  }
  return bullets;
}

// Pull section H2/H3 headings as TL;DR
function extractHeadings(md, max = 4) {
  if (!md) return [];
  const lines = String(md).split(/\r?\n/);
  const out = [];
  for (const ln of lines) {
    const m = ln.match(/^\s*##+\s+(.+?)\s*$/);
    if (m) out.push(m[1].replace(/\*\*/g, ''));
    if (out.length >= max) break;
  }
  return out;
}

function CollapsibleProse({ md, label = 'Full analysis' }) {
  if (!md) return null;
  return (
    <details className="mt-3">
      <summary className="cursor-pointer text-xs text-muted-foreground select-none">
        {label}
      </summary>
      <div className="mt-2">
        <MD>{md}</MD>
      </div>
    </details>
  );
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Statuses where the run is finished and there's nothing left to poll for.
const TERMINAL_STATUSES = ['completed', 'failed', 'cancelled', 'budget_exceeded'];

export default function CommitteeRunPage() {
  const { runId } = useParams();
  const navigate = useNavigate();
  const [bundle, setBundle] = useState(null);
  const [audit, setAudit] = useState(null);
  const [trackRecord, setTrackRecord] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // `silent` skips the full-screen spinner so background polling doesn't flicker
  // the page while an in-flight run advances through phases.
  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/committee/runs/${runId}`),
        axios.get(`${API}/committee/runs/${runId}/audit`).catch(() => ({ data: null })),
      ]);
      setBundle(r1.data);
      setAudit(r2.data);
      // Fire-and-forget: pull historical track record for this stock so the
      // verdict badge can show "BUY: 60% hit rate, +1.4% excess at 90d".
      const isin = r1.data?.summary?.isin;
      if (isin) {
        axios
          .get(`${API}/committee/track_record/${isin}?horizon_days=90`)
          .then((tr) => setTrackRecord(tr.data))
          .catch(() => setTrackRecord(null));
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh while the run is still in flight, so a page opened mid-run
  // updates itself instead of showing a stale snapshot until manual Refresh.
  const runStatus = bundle?.summary?.status;
  const isLive = !!runStatus && !TERMINAL_STATUSES.includes(runStatus);
  useEffect(() => {
    if (!isLive) return undefined;
    const id = setInterval(() => load(true), 5000);
    return () => clearInterval(id);
  }, [isLive, load]);

  const downloadPdf = async () => {
    if (downloadingPdf) return;
    setDownloadingPdf(true);
    try {
      // axios auto-attaches the JWT via attachAxiosAuthInterceptor; window.open
      // can't send headers, which is why the prior implementation 404'd on prod.
      const resp = await axios.get(`${API}/committee/runs/${runId}/pdf`, {
        responseType: 'blob',
        validateStatus: (s) => s >= 200 && s < 500,
      });
      if (resp.status === 401 || resp.status === 403) {
        alert('Your session expired. Please sign in again, then retry.');
        return;
      }
      if (resp.status === 404) {
        alert('Run not found for your account. If this run was triggered for a different tenant or has been deleted, you cannot export its PDF.');
        return;
      }
      if (resp.status >= 400) {
        const text = await resp.data.text().catch(() => '');
        alert(`PDF download failed (${resp.status}): ${text || 'unknown error'}`);
        return;
      }
      const ct = resp.headers['content-type'] || '';
      const isPdf = ct.includes('application/pdf');
      const blob = new Blob([resp.data], { type: isPdf ? 'application/pdf' : 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      if (isPdf) {
        const name = bundle?.company?.nse_symbol || bundle?.summary?.isin || runId;
        link.download = `committee_memo_${name}.pdf`;
        link.click();
      } else {
        // WeasyPrint missing on server — open HTML preview in a new tab instead.
        window.open(link.href, '_blank', 'noopener');
      }
      setTimeout(() => URL.revokeObjectURL(link.href), 2000);
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || 'Failed to download committee PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };
  const resume = async () => {
    setResuming(true);
    try {
      await axios.post(`${API}/committee/runs/${runId}/resume`);
      await load();
    } finally { setResuming(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }
  if (!bundle) return <div className="p-6">Run not found.</div>;

  const { summary, artifacts, decision, company } = bundle;
  const stockLabel = company?.nse_symbol || company?.company_name || summary.isin;
  const phases = groupArtifactsByPhase(artifacts);
  // Derive a {role: status, verdict} map for the live agent graph.
  // Per-role status is inferred from the artifact list shipped by the API;
  // when SSE phase events are wired in (future), swap to deriveAgentState().
  const agentStatuses = {};
  const agentVerdicts = {};
  for (const a of artifacts || []) {
    if (!a || !a.role) continue;
    agentStatuses[a.role] = a.error ? 'error' : 'complete';
    const v = a.structured_json && (a.structured_json.verdict || a.structured_json.recommendation);
    if (v) agentVerdicts[a.role] = v;
  }
  // Backend emits debate as bull_round_1/2, bear_round_1/2 (one row per round);
  // collapse them onto the single 'bull' / 'bear' nodes shown in AgentGraph.
  // Same idea for cio_synthesizer (kept for forward compat — current code uses
  // role='cio' directly, but that artifact may not exist when the decision is
  // emitted from committee_decision rather than committee_artifact).
  const ROLE_ALIASES = {
    bull_round_1: 'bull', bull_round_2: 'bull', bull_round_3: 'bull',
    bear_round_1: 'bear', bear_round_2: 'bear', bear_round_3: 'bear',
    cio_synthesizer: 'cio', cio_synthesis: 'cio',
  };
  for (const [src, dst] of Object.entries(ROLE_ALIASES)) {
    if (agentStatuses[src] && !agentStatuses[dst]) agentStatuses[dst] = agentStatuses[src];
    if (agentVerdicts[src] && !agentVerdicts[dst]) agentVerdicts[dst] = agentVerdicts[src];
  }
  if (summary && summary.status && summary.status !== 'completed' && summary.status !== 'failed') {
    // Highlight the current phase as "running" so a mid-flight run shows motion.
    const currentPhase = summary.current_phase;
    if (currentPhase && !agentStatuses[currentPhase]) {
      agentStatuses[currentPhase] = 'running';
    }
  }
  // A finalized decision implies the CIO synthesis ran successfully, even
  // though the CIO output is stored in committee_decision rather than as a
  // committee_artifact row — so the status loop above won't set it.
  if (decision && decision.verdict) {
    agentVerdicts.cio = decision.verdict;
    if (!agentStatuses.cio) agentStatuses.cio = 'complete';
  }

  const overlayCtx = bundle.overlay || null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate('/public/committee')}>
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex gap-2 items-center">
          {isLive && (
            <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 font-medium mr-1">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              Live · auto-refreshing
            </span>
          )}
          <Button variant="outline" onClick={() => load()}><RefreshCw className="w-4 h-4 mr-1" /> Refresh</Button>
          {summary.status !== 'completed' && (
            <Button onClick={resume} disabled={resuming}>
              {resuming ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
              Resume
            </Button>
          )}
          <Button onClick={downloadPdf} disabled={downloadingPdf}>
            {downloadingPdf
              ? <Loader2 className="w-4 h-4 mr-1 animate-spin" />
              : <Download className="w-4 h-4 mr-1" />}
            PDF
          </Button>
        </div>
      </div>

      <PageHeader title={`${stockLabel} — ${overlayCtx?.is_overlay ? 'House-View Overlay' : 'Committee Run'}`}
                  subtitle={`${company?.company_name && company?.nse_symbol ? `${company.company_name} • ` : ''}As of ${summary.as_of_date} • status: ${summary.status} • cost ${formatPaise(summary.total_cost_inr_paise)}`} />

      <TierComparisonCard overlayCtx={overlayCtx} decision={decision} />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
            Agent flow
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <AgentGraph statuses={agentStatuses} verdicts={agentVerdicts} />
        </CardContent>
      </Card>

      {decision && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-3 flex-wrap">
              <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-xs font-semibold ${classForVerdict(decision.verdict)}`}>
                {decision.verdict} {decision.conviction}/10
              </span>
              <TrackRecordBadge record={trackRecord} verdict={decision.verdict} />
              <span className="text-base font-medium flex-1">{decision.one_line_thesis}</span>
              {decision.compliance_status && decision.compliance_status !== 'pass' && (
                <Badge variant="outline" className="border-amber-500/30 text-amber-600">
                  {decision.compliance_status}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-4 gap-3 text-sm mb-4">
              <Stat label="Size" value={fmtPct(decision.size_pct_nav)} />
              <Stat label="Stop" value={fmtPct(decision.stop_pct)} />
              <Stat label="Target" value={fmtPct(decision.target_pct)} />
              <Stat label="Horizon" value={decision.horizon_days != null ? `${decision.horizon_days}d` : '–'} />
            </div>
            <MD>{decision.memo_md}</MD>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="analysts">
        <TabsList>
          <TabsTrigger value="analysts">Analysts</TabsTrigger>
          <TabsTrigger value="debate">Debate</TabsTrigger>
          <TabsTrigger value="trader">Trader</TabsTrigger>
          <TabsTrigger value="risk">Risk Panel</TabsTrigger>
          <TabsTrigger value="compliance">Compliance + CIO</TabsTrigger>
          <TabsTrigger value="audit">Audit</TabsTrigger>
        </TabsList>
        <TabsContent value="analysts">
          <ArtifactGroup artifacts={phases['analysts'] || []} />
        </TabsContent>
        <TabsContent value="debate">
          <ArtifactGroup artifacts={phases['debate'] || []} />
        </TabsContent>
        <TabsContent value="trader">
          <ArtifactGroup artifacts={phases['trader'] || []} />
        </TabsContent>
        <TabsContent value="risk">
          <ArtifactGroup artifacts={phases['risk_panel'] || []} />
        </TabsContent>
        <TabsContent value="compliance">
          <ArtifactGroup artifacts={phases['compliance_cio'] || []} />
        </TabsContent>
        <TabsContent value="audit">
          <AuditView audit={audit} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded border p-2 bg-muted/30">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-base font-semibold">{value}</div>
    </div>
  );
}

// One desk's verdict column inside the Platform-vs-House comparison card.
function DeskVerdict({ title, subtitle, verdict, conviction, sizePct, thesis, killCriteria, runId: deskRunId, highlight }) {
  return (
    <div className={`rounded-lg border p-3 space-y-2 ${highlight ? 'border-primary/40 bg-primary/5' : 'bg-muted/20'}`}>
      <div>
        <div className="text-xs uppercase tracking-wider text-muted-foreground">{subtitle}</div>
        <div className="text-sm font-semibold">{title}</div>
      </div>
      {verdict ? (
        <>
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-semibold ${classForVerdict(verdict)}`}>
              {verdict} {conviction != null ? `${conviction}/10` : ''}
            </span>
            {sizePct != null && (
              <span className="text-xs text-muted-foreground">size {Number(sizePct).toFixed(1)}% NAV</span>
            )}
          </div>
          {thesis && <p className="text-xs text-muted-foreground leading-relaxed">{thesis}</p>}
          {Array.isArray(killCriteria) && killCriteria.length > 0 && (
            <div className="text-[11px] text-muted-foreground">
              <span className="font-medium text-foreground">Kill criteria:</span>{' '}
              {killCriteria.slice(0, 2).map((k) => k.condition || k.description || '').filter(Boolean).join(' · ')}
              {killCriteria.length > 2 ? ` (+${killCriteria.length - 2} more)` : ''}
            </div>
          )}
        </>
      ) : (
        <p className="text-xs text-muted-foreground">No decision recorded.</p>
      )}
      {deskRunId && (
        <a href={`/public/committee/runs/${deskRunId}`} className="text-[11px] text-primary hover:underline block">
          View full run →
        </a>
      )}
    </div>
  );
}

// "Platform vs House View" — rendered when this run is a house-view overlay
// (compare against its source platform run) or when other desks have
// overlaid this platform run (compare against each house verdict).
function TierComparisonCard({ overlayCtx, decision }) {
  const [editOpen, setEditOpen] = useState(false);
  if (!overlayCtx) return null;
  const isOverlay = overlayCtx.is_overlay && overlayCtx.source;
  const hasOverlays = (overlayCtx.overlays || []).length > 0;
  if (!isOverlay && !hasOverlays) return null;

  return (
    <Card className="border-primary/30">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <Shield className="w-4 h-4" /> Platform vs House View
          <span className="normal-case font-normal text-xs">
            — same shared evidence, different mandates
          </span>
          {overlayCtx.can_edit_house_view && (
            <button
              onClick={() => setEditOpen(true)}
              className="ml-auto normal-case font-normal text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              <Pencil className="w-3 h-3" /> Edit house view
            </button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {isOverlay && (
            <>
              <DeskVerdict
                title={overlayCtx.source.tenant_name || 'Platform Research Desk'}
                subtitle="Platform (neutral mandate)"
                verdict={overlayCtx.source.verdict}
                conviction={overlayCtx.source.conviction}
                sizePct={overlayCtx.source.size_pct_nav}
                thesis={overlayCtx.source.one_line_thesis}
                killCriteria={overlayCtx.source.kill_criteria}
                runId={overlayCtx.source.run_id}
              />
              <DeskVerdict
                title={overlayCtx.tenant_name || 'House Desk'}
                subtitle="Institutional house view"
                verdict={decision?.verdict}
                conviction={decision?.conviction}
                sizePct={decision?.size_pct_nav}
                thesis={decision?.one_line_thesis}
                killCriteria={decision?.kill_criteria}
                highlight
              />
            </>
          )}
          {!isOverlay && hasOverlays && (
            <DeskVerdict
              title={overlayCtx.tenant_name || 'Platform Research Desk'}
              subtitle="Platform (neutral mandate)"
              verdict={decision?.verdict}
              conviction={decision?.conviction}
              sizePct={decision?.size_pct_nav}
              thesis={decision?.one_line_thesis}
              killCriteria={decision?.kill_criteria}
              highlight
            />
          )}
          {(overlayCtx.overlays || []).map((o) => (
            <DeskVerdict
              key={o.run_id}
              title={o.tenant_name || 'House Desk'}
              subtitle="Institutional house view"
              verdict={o.verdict}
              conviction={o.conviction}
              sizePct={o.size_pct_nav}
              thesis={o.one_line_thesis}
              killCriteria={o.kill_criteria}
              runId={o.run_id}
            />
          ))}
        </div>
        {overlayCtx.can_edit_house_view && (
          <p className="text-[11px] text-muted-foreground mt-3">
            Disagree with a rule in this verdict? Every mandate lever —
            valuation ceilings, quality floors, exits, exclusions — is
            editable. The committee re-judges this same evidence under
            whatever view you set.
          </p>
        )}
      </CardContent>
      {overlayCtx.tenant_id && (
        <EditHouseViewDialog
          tenantId={overlayCtx.tenant_id}
          open={editOpen}
          onOpenChange={setEditOpen}
        />
      )}
    </Card>
  );
}

function fmtPct(v) {
  if (v == null || v === '') return '–';
  const n = Number(v);
  if (!Number.isFinite(n)) return '–';
  return `${n.toFixed(n % 1 === 0 ? 0 : 2)}%`;
}

function roleTitle(role) {
  if (!role) return '';
  return String(role)
    .split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const STANCE_CLASS = {
  aggressive: 'bg-red-500/10 text-red-500 border-red-500/30',
  conservative: 'bg-blue-500/10 text-blue-500 border-blue-500/30',
  neutral: 'bg-muted text-muted-foreground border-muted-foreground/20',
};

const SEVERITY_CLASS = {
  high: 'border-red-500/30 text-red-500',
  medium: 'border-amber-500/30 text-amber-600',
  low: 'border-emerald-500/30 text-emerald-500',
};

function ArtifactHeader({ artifact, badges }) {
  const a = artifact;
  return (
    <CardTitle className="flex items-center justify-between gap-3 flex-wrap">
      <div className="flex items-center gap-2 min-w-0">
        <span className="font-semibold">{roleTitle(a.role)}</span>
        {badges}
      </div>
      <span className="text-xs text-muted-foreground font-normal whitespace-nowrap">
        {a.tokens_in}+{a.tokens_out} toks • {formatPaise(a.cost_inr_paise)}
        {a.duration_ms != null && ` • ${(a.duration_ms / 1000).toFixed(1)}s`}
      </span>
    </CardTitle>
  );
}

function ToolsCalledList({ tools }) {
  if (!tools || tools.length === 0) return null;
  return (
    <details className="mt-3 text-xs">
      <summary className="cursor-pointer text-muted-foreground">
        Tools called ({tools.length})
      </summary>
      <div className="mt-2 space-y-1 font-mono">
        {tools.map((t, i) => (
          <div key={i} className="flex gap-2">
            <span className="text-blue-500">{t.name}</span>
            <span className="text-muted-foreground truncate">{t.result_summary}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

// ── Per-role viewers ────────────────────────────────────────

function AnalystArtifact({ a }) {
  const s = a.structured_json || {};
  const body = s.body_md || stripJsonFence(a.content_md);
  const bullets = extractBullets(body, 5);
  const sections = extractHeadings(body, 4);
  return (
    <Card>
      <CardHeader>
        <ArtifactHeader artifact={a}
          badges={typeof s.confidence === 'number' && (
            <Badge variant="outline">conf {s.confidence}/10</Badge>
          )} />
      </CardHeader>
      <CardContent>
        {s.headline && (
          <p className="text-sm font-medium mb-3 text-foreground">{s.headline}</p>
        )}
        <NumericFactChips facts={s.key_facts} />
        {bullets.length > 0 && (
          <ul className="text-sm space-y-1 list-disc pl-5">
            {bullets.map((b, i) => <li key={i}>{b}</li>)}
          </ul>
        )}
        {sections.length > 0 && (
          <div className="mt-2 text-[11px] text-muted-foreground">
            Sections: {sections.join(' · ')}
          </div>
        )}
        <CollapsibleProse md={body} />
        <FactsList facts={s.key_facts} />
        <ToolsCalledList tools={a.tools_called} />
      </CardContent>
    </Card>
  );
}

function DebateArtifact({ a }) {
  const s = a.structured_json || {};
  const isBull = (a.role || '').includes('bull');
  const Icon = isBull ? TrendingUp : TrendingDown;
  const tint = isBull ? 'text-emerald-500' : 'text-red-500';
  return (
    <Card>
      <CardHeader>
        <ArtifactHeader artifact={a} badges={
          <span className={`inline-flex items-center gap-1 ${tint}`}>
            <Icon className="w-4 h-4" />
            {s.round && <span className="text-xs">Round {s.round}</span>}
          </span>
        } />
      </CardHeader>
      <CardContent>
        {Array.isArray(s.claim_ids_attacked) && s.claim_ids_attacked.length > 0 && (
          <div className="text-xs text-muted-foreground mb-2">
            Rebuts: {s.claim_ids_attacked.map((c) => (
              <span key={c} className="font-mono ml-1 text-amber-600">[{c}]</span>
            ))}
          </div>
        )}
        {Array.isArray(s.new_claims) && s.new_claims.length > 0 && (
          <ul className="text-sm space-y-1 list-disc pl-5">
            {s.new_claims.slice(0, 5).map((c, i) => (
              <li key={c.claim_id || i}>
                <span className="font-mono text-muted-foreground text-xs">[{c.claim_id}]</span>{' '}
                {c.text}
              </li>
            ))}
          </ul>
        )}
        <CollapsibleProse md={s.body_md || stripJsonFence(a.content_md)} label="Full argument" />
      </CardContent>
    </Card>
  );
}

function TraderArtifact({ a }) {
  const s = a.structured_json || {};
  const dirClass = s.direction === 'LONG' ? 'text-emerald-500'
                  : s.direction === 'SHORT' ? 'text-red-500'
                  : 'text-muted-foreground';
  return (
    <Card>
      <CardHeader>
        <ArtifactHeader artifact={a} badges={
          <>
            {s.direction && <Badge variant="outline" className={dirClass}>{s.direction}</Badge>}
            {typeof s.conviction === 'number' && <Badge variant="outline">conv {s.conviction}/10</Badge>}
            {s.illiquidity_flag && <Badge variant="outline" className="border-amber-500/30 text-amber-600">illiquid</Badge>}
            {s.fno_aware && <Badge variant="outline">F&amp;O</Badge>}
            {s.sebi_category_flag && <Badge variant="outline" className="border-red-500/30 text-red-500">{s.sebi_category_flag}</Badge>}
          </>
        } />
      </CardHeader>
      <CardContent>
        {s.direction && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3 text-sm">
            <Stat label="Size" value={fmtPct(s.size_pct_nav)} />
            <Stat label="Stop" value={fmtPct(s.stop_pct)} />
            <Stat label="Target" value={fmtPct(s.target_pct)} />
            <Stat label="Horizon" value={s.horizon_days != null ? `${s.horizon_days}d` : '–'} />
            <Stat label="Time-sensitive" value={s.time_sensitive ? 'Yes' : 'No'} />
            {s.liquidity_score != null && (
              <Stat label="Liquidity" value={`${Number(s.liquidity_score).toFixed(0)}/100`} />
            )}
            {s.avg_daily_value_traded_inr != null && (
              <Stat
                label="Avg traded"
                value={`₹${(Number(s.avg_daily_value_traded_inr) / 10000000).toLocaleString('en-IN', { maximumFractionDigits: 1 })} cr`}
              />
            )}
          </div>
        )}
        {s.entry_hint && (
          <p className="text-sm mb-2"><span className="text-muted-foreground">Entry:</span> {s.entry_hint}</p>
        )}
        {s.rationale && <p className="text-sm">{s.rationale}</p>}
        <CollapsibleProse md={stripJsonFence(a.content_md)} label="Full reasoning" />
        <ToolsCalledList tools={a.tools_called} />
      </CardContent>
    </Card>
  );
}

function RiskArtifact({ a }) {
  const s = a.structured_json || {};
  const stance = s.stance;
  const sev = s.severity || s.overall_risk_level;
  return (
    <Card>
      <CardHeader>
        <ArtifactHeader artifact={a} badges={
          <>
            {stance && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-semibold ${STANCE_CLASS[stance] || ''}`}>
                {stance}
              </span>
            )}
            {sev && (
              <Badge variant="outline" className={SEVERITY_CLASS[sev] || ''}>{sev} risk</Badge>
            )}
          </>
        } />
      </CardHeader>
      <CardContent>
        {Array.isArray(s.key_concerns) && s.key_concerns.length > 0 && (
          <div>
            <div className="text-xs font-semibold text-muted-foreground mb-1">Key concerns</div>
            <ul className="text-sm list-disc pl-5 space-y-0.5">
              {s.key_concerns.slice(0, 5).map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}
        {Array.isArray(s.suggested_adjustments) && s.suggested_adjustments.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-semibold text-muted-foreground mb-1">Suggested adjustments</div>
            <ul className="text-sm list-disc pl-5 space-y-0.5">
              {s.suggested_adjustments.slice(0, 5).map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}
        {Array.isArray(s.points_of_agreement) && s.points_of_agreement.length > 0 && (
          <div className="mt-3 text-sm">
            <div className="text-xs font-semibold text-emerald-600 mb-1">Agreement</div>
            <ul className="list-disc pl-5 space-y-0.5">
              {s.points_of_agreement.slice(0, 5).map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}
        {Array.isArray(s.points_of_disagreement) && s.points_of_disagreement.length > 0 && (
          <div className="mt-3 text-sm">
            <div className="text-xs font-semibold text-amber-600 mb-1">Disagreement</div>
            <ul className="list-disc pl-5 space-y-0.5">
              {s.points_of_disagreement.slice(0, 5).map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}
        <CollapsibleProse md={s.body_md || stripJsonFence(a.content_md)} />
      </CardContent>
    </Card>
  );
}

function ComplianceArtifact({ a }) {
  const s = a.structured_json || {};
  const passes = s.passes;
  const Icon = passes === false ? XCircle : passes === true ? CheckCircle2 : AlertTriangle;
  const tint = passes === false ? 'text-red-500'
              : passes === true ? 'text-emerald-500' : 'text-amber-500';
  return (
    <Card>
      <CardHeader>
        <ArtifactHeader artifact={a} badges={
          <span className={`inline-flex items-center gap-1 ${tint}`}>
            <Icon className="w-4 h-4" />
            <span className="text-xs">{passes === false ? 'Fail' : passes === true ? 'Pass' : 'Review'}</span>
          </span>
        } />
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {[
            ['Restricted', s.restricted_list_hit],
            ['Sector cap', s.sector_cap_hit],
            ['MCap floor', s.mcap_floor_hit],
            ['Max position', s.max_position_hit],
          ].map(([label, v]) => (
            <div key={label} className={`rounded border p-2 ${v ? 'border-red-500/30 text-red-500' : 'border-emerald-500/20 text-muted-foreground'}`}>
              <div className="font-medium">{label}</div>
              <div>{v ? 'breached' : 'ok'}</div>
            </div>
          ))}
        </div>
        {Array.isArray(s.violations) && s.violations.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-semibold text-red-500 mb-1">Violations</div>
            <ul className="text-sm list-disc pl-5 space-y-0.5">
              {s.violations.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}
        {Array.isArray(s.mandatory_adjustments) && s.mandatory_adjustments.length > 0 && (
          <div className="mt-3">
            <div className="text-xs font-semibold text-amber-600 mb-1">Mandatory adjustments</div>
            <ul className="text-sm list-disc pl-5 space-y-0.5">
              {s.mandatory_adjustments.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}
        <CollapsibleProse md={s.body_md || stripJsonFence(a.content_md)} />
      </CardContent>
    </Card>
  );
}

function CIOArtifact({ a }) {
  const s = a.structured_json || {};
  return (
    <Card>
      <CardHeader>
        <ArtifactHeader artifact={a} badges={
          <>
            {s.verdict && (
              <span className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-semibold ${classForVerdict(s.verdict)}`}>
                {s.verdict}
                {typeof s.conviction === 'number' && <span className="ml-1 opacity-70">{s.conviction}/10</span>}
              </span>
            )}
            {s.view_changed_from_last_run && (
              <Badge variant="outline" className="border-amber-500/30 text-amber-600">View Δ</Badge>
            )}
          </>
        } />
      </CardHeader>
      <CardContent>
        {s.one_line_thesis && (
          <p className="text-sm font-medium mb-3">{s.one_line_thesis}</p>
        )}
        {Array.isArray(s.change_drivers) && s.change_drivers.length > 0 && (
          <div className="mb-3 text-sm">
            <div className="text-xs font-semibold text-muted-foreground mb-1">Change drivers</div>
            <ul className="list-disc pl-5 space-y-0.5">
              {s.change_drivers.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          </div>
        )}
        <p className="text-xs text-muted-foreground italic">
          Full IC memo is shown in the verdict card at the top of this page.
        </p>
        <CollapsibleProse md={s.memo_md || stripJsonFence(a.content_md)} label="Re-show full memo" />
      </CardContent>
    </Card>
  );
}

function GenericArtifact({ a }) {
  return (
    <Card>
      <CardHeader>
        <ArtifactHeader artifact={a} />
      </CardHeader>
      <CardContent>
        <MD>{stripJsonFence(a.content_md)}</MD>
        <ToolsCalledList tools={a.tools_called} />
      </CardContent>
    </Card>
  );
}

function ArtifactGroup({ artifacts }) {
  if (!artifacts || artifacts.length === 0) {
    return <Card><CardContent className="p-6 text-sm text-muted-foreground">No artifacts in this phase.</CardContent></Card>;
  }
  return (
    <div className="space-y-3">
      {artifacts.map((a) => {
        const role = a.role || '';
        const phase = a.phase || '';
        if (phase === 'analysts') return <AnalystArtifact key={a.id} a={a} />;
        if (phase === 'debate') return <DebateArtifact key={a.id} a={a} />;
        if (phase === 'trader') return <TraderArtifact key={a.id} a={a} />;
        if (phase === 'risk_panel') return <RiskArtifact key={a.id} a={a} />;
        if (phase === 'compliance_cio' && role === 'compliance') return <ComplianceArtifact key={a.id} a={a} />;
        if (phase === 'compliance_cio' && role === 'cio') return <CIOArtifact key={a.id} a={a} />;
        return <GenericArtifact key={a.id} a={a} />;
      })}
    </div>
  );
}

function AuditView({ audit }) {
  if (!audit || !audit.claims) {
    return <Card><CardContent className="p-6 text-sm text-muted-foreground">Audit ledger not available.</CardContent></Card>;
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2"><Shield className="w-4 h-4" /> Provenance Ledger ({audit.count} claims)</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-auto">
          <table className="text-xs w-full">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="px-2 py-1">Claim</th>
                <th className="px-2 py-1">Role</th>
                <th className="px-2 py-1">Tool</th>
                <th className="px-2 py-1">DB Ref</th>
                <th className="px-2 py-1">Text</th>
              </tr>
            </thead>
            <tbody>
              {audit.claims.map((c, i) => (
                <tr key={i} className="border-t">
                  <td className="px-2 py-1 font-mono">{c.claim_id}</td>
                  <td className="px-2 py-1">{c.role}</td>
                  <td className="px-2 py-1 text-blue-500">{c.tool_name}</td>
                  <td className="px-2 py-1 font-mono text-muted-foreground">{c.db_row_ref}</td>
                  <td className="px-2 py-1">{c.claim_text}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
